/**
 * A page of each colour lineup, with a web address of its own.
 *
 * Jonathon, 2026-09-30: "sometimes we want to send all of these color options
 * to customers via links", then "yes build that". Until then every lineup was
 * only reachable from inside a proposal, behind its "View colors" card.
 *
 * So each lineup the proposal can show gets a page at `/colors/<slug>`, and
 * `/colors` lists them all. The page shows exactly what the card on a
 * proposal opens: the same viewer, the same photo chart, or the same chart
 * the card links to. Nothing here is new data; it reads `SHINGLE_LINES`,
 * `GUTTER_COLORS` and `SIDING_LINES`, so a lineup added there gets a page by
 * adding one row below.
 *
 * **A slug is never renamed or removed once it has shipped.** It is in
 * customers' text messages and emails, like the files a frozen proposal
 * points at (ADR 0237). A product that changes keeps its slug; a new one gets
 * a new slug. The slugs are words a customer can read, not the internal keys:
 * the Ag Panel's key is "exposed-fastener", which says nothing to anybody.
 */

import { GUTTER_COLORS, type GutterColor } from "./gutterColors.js";
import {
  SHINGLE_COLOR_NOTE,
  SHINGLE_LINES,
  type ColorViewer,
  type ShingleColor,
} from "./shingleColors.js";
import { SIDING_LINES } from "./sidingColors.js";

/** What a colour page shows. */
export type ColorPageContent =
  /** The swipeable viewer a proposal's card opens. */
  | { kind: "viewer"; viewer: ColorViewer }
  /** A chart of photographs, under the folder they live in. */
  | {
      kind: "chart";
      folder: "shingle-colors" | "gutter-colors";
      colors: readonly (ShingleColor | GutterColor)[];
      /** What the photographs show, for anybody who cannot see them. */
      what: string;
    }
  /** A chart somebody else publishes, or a PDF of ours: the page goes there. */
  | { kind: "link"; url: string };

/** One lineup's page. */
export interface ColorPage {
  /** The last part of its web address. Never renamed once shipped. */
  slug: string;
  /** What it is called on the list and over the page. */
  label: string;
  group: "Shingles" | "Metal Roofing" | "Gutters" | "Siding";
  /**
   * Which lineup it is the page of: `shingles/<key>` for a `SHINGLE_LINES`
   * row, `siding/<key>` for a `SIDING_LINES` row, or `gutters`. Never shown;
   * it is how a test knows every lineup has a page, by name.
   */
  lineup: string;
  content: ColorPageContent;
}

type LineupContent = Pick<ColorPage, "lineup" | "content">;

/** The lineup behind a key, as the page it becomes. */
function shingleContent(key: string): LineupContent {
  const line = SHINGLE_LINES.find((candidate) => candidate.key === key);
  if (!line) throw new Error(`No colour lineup "${key}"`);
  const lineup = `shingles/${key}`;
  if (line.viewer) {
    return { lineup, content: { kind: "viewer", viewer: line.viewer } };
  }
  if (line.colorsUrl) {
    return { lineup, content: { kind: "link", url: line.colorsUrl } };
  }
  return {
    lineup,
    content: {
      kind: "chart",
      folder: "shingle-colors",
      colors: line.colors,
      what: `${line.shingle} roof`,
    },
  };
}

function sidingContent(key: string): LineupContent {
  const line = SIDING_LINES.find((candidate) => candidate.key === key);
  if (!line) throw new Error(`No siding lineup "${key}"`);
  return {
    lineup: `siding/${key}`,
    content: { kind: "viewer", viewer: line.viewer },
  };
}

/**
 * Every page, in the order the list shows them: roofs first, the way a
 * proposal reads, then what goes on the walls.
 */
export const COLOR_PAGES: readonly ColorPage[] = [
  {
    slug: "timberline-hdz",
    label: "GAF Timberline HDZ",
    group: "Shingles",
    ...shingleContent("hdz"),
  },
  {
    slug: "timberline-uhdz",
    label: "GAF Timberline UHDZ",
    group: "Shingles",
    ...shingleContent("uhdz"),
  },
  {
    slug: "titan-xt",
    label: "TAMKO Titan XT",
    group: "Shingles",
    ...shingleContent("titan-xt"),
  },
  {
    slug: "timbersteel",
    label: "GAF TimberSteel",
    group: "Metal Roofing",
    ...shingleContent("timbersteel"),
  },
  {
    slug: "standing-seam",
    label: "Standing Seam Metal",
    group: "Metal Roofing",
    ...shingleContent("standing-seam"),
  },
  {
    slug: "ag-panel",
    label: "Ag Panel Metal",
    group: "Metal Roofing",
    ...shingleContent("exposed-fastener"),
  },
  {
    slug: "r-panel",
    label: "R Panel Metal",
    group: "Metal Roofing",
    ...shingleContent("r-panel"),
  },
  {
    slug: "gutters",
    label: "Seamless Gutters",
    group: "Gutters",
    lineup: "gutters",
    content: {
      kind: "chart",
      folder: "gutter-colors",
      colors: GUTTER_COLORS,
      what: "seamless gutters",
    },
  },
  {
    slug: "vinyl-siding",
    label: "Standard Vinyl Siding",
    group: "Siding",
    ...sidingContent("parkview"),
  },
];

/** The note every page of photographs carries, as the proposal's does. */
export const COLOR_PAGE_NOTE = SHINGLE_COLOR_NOTE;

/** Where the list of every page lives. */
export const COLOR_PAGES_PATH = "/colors";

/** A page's web address, without the site: `/colors/standing-seam`. */
export const colorPagePath = (page: Pick<ColorPage, "slug">): string =>
  `${COLOR_PAGES_PATH}/${page.slug}`;

/**
 * Which colour page a path asks for.
 *
 * `""` for the list itself, a slug for one page (known or not; the page says
 * when it is not), and null for any path that is not a colour page at all.
 */
export function colorSlugFromPath(pathname: string): string | null {
  const match = /^\/colors(?:\/([a-z0-9-]+))?\/?$/.exec(pathname);
  if (!match) return null;
  return match[1] ?? "";
}

/** The page for a slug, or null when there is none. */
export const colorPageFor = (slug: string): ColorPage | null =>
  COLOR_PAGES.find((page) => page.slug === slug) ?? null;
